import React, { useEffect, useState } from 'react';
import { Mail, Save, Send, RefreshCw } from 'lucide-react';
import Card from './Card';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import Alert from './Alert';
import { eventProcessingService, processingError, CompanyEmailSettings } from '../../services/event-processing.service';

const CompanyNotificationEmail: React.FC = () => {
  const [settings, setSettings] = useState<CompanyEmailSettings | null>(null);
  const [password, setPassword] = useState('');
  const [recipients, setRecipients] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    eventProcessingService.getEmailSettings().then(data => {
      if (active) { setSettings(data); setRecipients(data.recipients.join(', ')); }
    }).catch(err => { if (active) setError(processingError(err)); });
    return () => { active = false; };
  }, []);
  const change = (patch: Partial<CompanyEmailSettings>) => {
    setSettings(current => current ? { ...current, ...patch } : current); setDirty(true); setMessage('');
  };
  const save = async () => {
    if (!settings) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const { password_configured, recent_deliveries, ...config } = settings;
      const data = await eventProcessingService.saveEmailSettings({ ...config, password: password || undefined,
        recipients: recipients.split(/[,;\n]/).map(value => value.trim()).filter(Boolean) });
      setSettings(data); setRecipients(data.recipients.join(', ')); setPassword(''); setDirty(false); setMessage('Company email settings saved.');
    } catch (err) { setError(processingError(err)); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setError('');
    try { const result = await eventProcessingService.testEmail(); setMessage(result.message); }
    catch (err) { setError(processingError(err)); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    try { const result = await eventProcessingService.getEmailSettings(); setSettings(current => current ? { ...current, recent_deliveries: result.recent_deliveries } : current); }
    catch (err) { setError(processingError(err)); }
  };

  return <Card padding="lg">
    <div className="flex items-start gap-3 mb-5"><div className="p-2 rounded-xl bg-accent-primary/10 text-accent-primary"><Mail size={22} /></div>
      <div><h2 className="text-lg font-semibold text-text-primary">Notification email</h2>
        <p className="text-sm text-text-muted mt-1">Send camera scenario alerts using your company’s SMTP server.</p></div>
    </div>
    {error && <Alert variant="error" message={error} />}
    {message && <Alert variant="success" message={message} />}
    {!settings ? <p className="text-sm text-text-muted mt-3">{error ? 'Company administrators can manage notification email.' : 'Loading email settings…'}</p> : <div className="space-y-5 mt-4">
      <label className="flex items-center gap-3 text-sm font-medium text-text-primary"><input type="checkbox" checked={settings.enabled} onChange={e => change({ enabled: e.target.checked })} className="w-4 h-4" />Enable email notifications for this company</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input label="SMTP host" placeholder="smtp.example.com" value={settings.smtp_host} onChange={e => change({ smtp_host: e.target.value })} fullWidth />
        <Input label="SMTP port" type="number" min={1} max={65535} value={settings.smtp_port} onChange={e => change({ smtp_port: Number(e.target.value) })} fullWidth />
        <Select label="Connection security" value={settings.security} onChange={e => change({ security: e.target.value as 'ssl' | 'starttls' })}
          options={[{ value: 'starttls', label: 'STARTTLS (usually port 587)' }, { value: 'ssl', label: 'TLS (usually port 465)' }]} fullWidth />
        <Input label="SMTP username" autoComplete="off" value={settings.username} onChange={e => change({ username: e.target.value })} helperText="Leave blank if your server uses an authenticated relay." fullWidth />
        <Input label="SMTP password" type="password" autoComplete="new-password" value={password} placeholder={settings.password_configured ? 'Password saved • enter a new password to replace' : 'Enter SMTP password'} onChange={e => { setPassword(e.target.value); setDirty(true); }} fullWidth />
        <Input label="Sender name" value={settings.from_name} onChange={e => change({ from_name: e.target.value })} fullWidth />
        <Input label="Sender email" type="email" placeholder="alerts@example.com" value={settings.from_email} onChange={e => change({ from_email: e.target.value })} fullWidth />
        <Input label="Recipients" placeholder="security@example.com, manager@example.com" value={recipients} onChange={e => { setRecipients(e.target.value); setDirty(true); }} helperText="Separate addresses with commas. Up to 20 recipients." fullWidth />
      </div>
      <p className="text-xs text-text-muted">SMTP passwords are encrypted and never returned to the browser. Camera notification rules decide when an alert is sent; turning email off keeps in-app alerts available.</p>
      <div className="flex flex-wrap gap-3">
        <Button type="button" isLoading={busy} leftIcon={<Save size={16} />} onClick={save}>Save email settings</Button>
        <Button type="button" variant="secondary" disabled={busy || dirty || !settings.enabled} leftIcon={<Send size={16} />} onClick={test}>Send test email</Button>
      </div>
      <div className="border-t border-border-default pt-4">
        <div className="flex items-center justify-between gap-3"><h3 className="text-sm font-medium text-text-primary">Recent email deliveries</h3><Button type="button" variant="ghost" size="sm" onClick={refresh} leftIcon={<RefreshCw size={14} />}>Refresh</Button></div>
        {!settings.recent_deliveries?.length ? <p className="text-xs text-text-muted mt-2">No email deliveries yet.</p> : <ul className="mt-2 space-y-2">{settings.recent_deliveries.map(item => <li key={item.id} className="flex flex-wrap justify-between gap-2 text-xs rounded-lg bg-surface-secondary px-3 py-2">
          <span className="text-text-muted">{new Date(item.created_at.endsWith('Z') ? item.created_at : item.created_at + 'Z').toLocaleString()}</span>
          <span className={item.email_status === 'failed' ? 'text-status-danger' : 'text-text-primary'}>{item.email_status === 'waiting' ? 'Queued / retry scheduled' : item.email_status}</span>
          {item.email_error && <span className="w-full text-status-danger">{item.email_error}</span>}
        </li>)}</ul>}
      </div>
    </div>}
  </Card>;
};

export default CompanyNotificationEmail;
